/**
 * `POST /api/stripe/connect/onboard` — start (or resume) Stripe Connect
 * onboarding for the current user. Mints a v2 `account_token` server-side
 * and creates the Connect account; subsequent calls reuse the existing row.
 *
 * Response is the new (or existing) Stripe account id plus a flag telling
 * the client to mount the embedded onboarding iframe — the only place KYC
 * lives. We do NOT return a hosted-onboarding URL because Accounts v2 +
 * embedded components are the canonical path.
 *
 * @see app/api/stripe/connect/onboard/route.ts — handler
 * @see components/settings/stripe/StripePageClient.tsx — web consumer
 * @see apps/mobile/lib/api-stripe-connect.ts — mobile consumer
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface StripeConnectOnboardRequest {
  /** ISO2 country code, lower-case. Required on first onboarding, ignored after. */
  country?: string;
}

export interface StripeConnectOnboardResponse {
  accountId: string;
  /** Always true today — kept as a field so adding hosted-link fallback later doesn't break clients. */
  requiresEmbedded: true;
}

export const StripeConnectOnboardRequestSchema = pinned<StripeConnectOnboardRequest>()(
  z.object({
    country: z.string().min(2).max(2).optional(),
  }),
);

export const StripeConnectOnboardResponseSchema = pinned<StripeConnectOnboardResponse>()(
  z.object({
    accountId: z.string(),
    requiresEmbedded: z.literal(true),
  }),
);
